import { useCallback, useRef, useState } from 'react';
import { formatDuration } from '../lib/format.ts';

interface Props {
  currentTime: number;
  duration: number;
  /** End of the downloaded-ahead region, drawn as a paler bar behind the fill. */
  bufferedTo: number;
  onSeek: (time: number) => void;
}

/** 4px track with an accent-light fill, draggable to seek (pointer events cover
 *  mouse and touch in one path). */
export function Scrubber({ currentTime, duration, bufferedTo, onSeek }: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragTime, setDragTime] = useState<number | null>(null);

  const timeAt = useCallback(
    (clientX: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || !duration) return 0;
      const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      return ratio * duration;
    },
    [duration],
  );

  // While dragging, show the dragged position rather than the (lagging) video time.
  const shown = dragTime ?? currentTime;
  const percent = duration > 0 ? Math.min((shown / duration) * 100, 100) : 0;
  const bufferedPercent = duration > 0 ? Math.min((bufferedTo / duration) * 100, 100) : 0;

  return (
    <div className="scrubber">
      <div
        ref={trackRef}
        className="scrubber__track"
        role="slider"
        tabIndex={0}
        aria-label="Перемотка"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(shown)}
        aria-valuetext={formatDuration(shown)}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragTime(timeAt(event.clientX));
        }}
        onPointerMove={(event) => {
          if (dragTime === null) return;
          setDragTime(timeAt(event.clientX));
        }}
        onPointerUp={(event) => {
          const time = timeAt(event.clientX);
          setDragTime(null);
          onSeek(time);
        }}
        onPointerCancel={() => setDragTime(null)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') onSeek(Math.max(shown - 5, 0));
          if (event.key === 'ArrowRight') onSeek(Math.min(shown + 5, duration));
        }}
      >
        <div className="scrubber__buffer" style={{ width: `${bufferedPercent}%` }} />
        <div className="scrubber__fill" style={{ width: `${percent}%` }} />
      </div>
      <div className="scrubber__times">
        <span>{formatDuration(shown)}</span>
        <span>{formatDuration(duration)}</span>
      </div>
    </div>
  );
}
